import { API_BASE_URL } from '@synergy/config';
import { Box, Paper, Stack, TableCell, TableRow, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Localized, LocalizedHtml } from '@synergy/components';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import {
    PoolButton,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolTypography,
    PoolVirtualTable,
} from '@synergy/components-next';
import { useParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { DemandItem } from '../../models/demand-item';

const StyledTableCell = styled(TableCell)(() => ({
    height: '32px',
    paddingTop: '6px',
    paddingBottom: '6px',
}));

interface DemandDetailDemandItemQuantityModalProps {
    open: boolean;
    demandItems: DemandItem[];
    translationId: string;
    onClose: () => void;
}

export const DemandDetailDemandItemQuantityModal = (props: DemandDetailDemandItemQuantityModalProps) => {
    const { open, onClose, translationId, demandItems } = props;
    const idParamString = useParams<{ id: string }>();

    const downloadList = () => {
        if (!idParamString) {
            return;
        }
        window.open(urlJoin(API_BASE_URL, `/demands/${idParamString.id}/xlsx`), '_blank');
        onClose();
    };

    return (
        <PaloNextThemeProvider>
            <PoolModal open={open} onClose={onClose} maxWidth="xl">
                <PoolModalTitle>
                    <FormattedMessage id={translationId} />
                </PoolModalTitle>
                <PoolModalContent sx={{ py: 2 }}>
                    <Paper elevation={0} sx={{ height: '582px', width: '1104px' }}>
                        <PoolVirtualTable
                            data={demandItems}
                            fixedHeaderContent={() => (
                                <TableRow sx={{ background: theme => theme.palette.grey[200] }}>
                                    <StyledTableCell>
                                        <FormattedMessage id="app.priceMatrix.positionNumber" />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <FormattedMessage id="app.table.column.product" />
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ textAlign: 'right' }}>
                                        <FormattedMessage id={'app.demands.detail.totalQuantity'} />
                                    </StyledTableCell>
                                </TableRow>
                            )}
                            rowRenderer={(demandItem, index) => (
                                <>
                                    <TableCell>
                                        <PoolTypography typography="subtitle1">
                                            {(demandItem.position ?? index) + 1}
                                        </PoolTypography>
                                    </TableCell>
                                    <TableCell>
                                        <PoolTypography typography="body1">
                                            <Stack flexDirection="column" gap={0.5}>
                                                <Box>
                                                    <Localized value={demandItem.name} />
                                                </Box>
                                                <Box color="grey.800">
                                                    <LocalizedHtml value={demandItem.longDescription} />
                                                </Box>
                                                <Box color="grey.800">
                                                    <FormattedMessage id="app.common.articleNumber" />{' '}
                                                    {demandItem.productNumber}
                                                </Box>
                                            </Stack>
                                        </PoolTypography>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <PoolTypography typography="body2">
                                            <Box color="grey.800">{demandItem.orderUnit}</Box>
                                        </PoolTypography>
                                        <PoolTypography typography="body1">
                                            <Box>{demandItem.totalQuantity ?? 0}</Box>
                                        </PoolTypography>
                                    </TableCell>
                                </>
                            )}
                        />
                    </Paper>
                </PoolModalContent>
                <PoolModalActions>
                    <PoolButton variant="text" onClick={downloadList}>
                        <FormattedMessage id="app.demands.detail.downloadList" />
                    </PoolButton>
                    <PoolButton variant="contained" onClick={onClose}>
                        <FormattedMessage id="app.common.close" />
                    </PoolButton>
                </PoolModalActions>
            </PoolModal>
        </PaloNextThemeProvider>
    );
};
